import { NoIndexHelmet } from "../seo";

const Meta = () => {
  return (
    <NoIndexHelmet>
      <title>Privacy Policy | Rujira</title>
      <meta
        name="description"
        content="Read the Rujira Privacy Policy to understand how information is collected, used, stored, and protected when using Rujira."
      />
      <meta
        name="og:description"
        content="Read the Rujira Privacy Policy to understand how information is collected, used, stored, and protected when using Rujira."
      />
      <meta
        name="twitter:description"
        content="Read the Rujira Privacy Policy to understand how information is collected, used, stored, and protected when using Rujira."
      />
    </NoIndexHelmet>
  );
};

export const PrivacyPolicy = () => (
  <>
    <Meta />
    <div className="rujira__main rujira__main--gradient">
      <div className="rujira__inner rujira__inner--pad">
        <h1 className="h1">Privacy Poilicy</h1>
        <p className="p">Last updated: 16 April 2025</p>
        <p className="p">
          This Privacy Policy (the "Policy”) explains how Ruji Holdings Limited
          and its affiliates ("us", "we" or "the Company") may collect, use and
          process information we may collect from users of the website-hosted
          user interface located at https://rujira.network/ and any associated
          subdomains and documentation (the "Interface”). For the purposes of
          this Policy, "you” and "your” refer to you as the user of the
          Interface.
        </p>
        <p className="p">
          Please read this Policy carefully and use it to make informed
          decisions. By using the Interface, you agree to the terms of this
          Policy and your continued use of the Interface constitutes your
          ongoing agreement to the Policy. If you do not agree to this Policy,
          do not use, access, connect to or interact with the Interface, or
          otherwise provide your information to us.
        </p>
        <h3 className="h4 mt-4">Collection of Information</h3>
        <p className="p">
          We may collect the following information about you when you use the
          Interface:
        </p>
        <ul>
          <li className="p">
            If and when you communicate with us, such as via feedback, support
            inquiries or other messages, we may collect your name, contact
            details, and any other information you include in your
            communication.
          </li>
          <li className="p">
            We use a third-party service named Plausible Analytics to collect
            anonymous, aggregated usage data for statistical purposes. Plausible
            does not use cookies or collect any personal data. This is to track
            overall trends in our website traffic, not to track individual
            visitors. Data collected includes referral sources, top pages, visit
            duration, information from the devices (device type, operating
            system, country and browser) used during the visit. Read more in the
            Plausible Analytics' data policy:{" "}
            <a
              href="https://plausible.io/data-policy"
              target="_blank"
              className="color-white hover-primary1">
              https://plausible.io/data-policy
            </a>
          </li>
        </ul>
        <h3 className="h4 mt-4">Use of Information</h3>
        <p className="p">
          We may collect and use information for business purposes in accordance
          with the practices described in this Policy and the Terms of Use,
          which include:
        </p>
        <ul>
          <li className="p">
            To make the Interface available to you and perform services
            requested by you, such as responding to communications;
          </li>
          <li className="p">
            To improve the Interface and fulfil any other legitimate business
            purpose as permitted under applicable laws;
          </li>
          <li className="p">
            In connection with, or during negotiations of, any proposed or
            actual merger, purchase, sale, or any other type of acquisition,
            financing, reorganization, or business combination of all or any
            portion of our assets, or transfer of all of a portion of our
            business to another business;
          </li>
          <li className="p">
            To detect, prevent, and address fraud, illegal activity or any other
            type of activity that may jeopardize or negatively affect the
            integrity of the Interface;
          </li>
          <li className="p">
            To address and solve any security risks;
          </li>
          <li className="p">
            To comply with lawful requests and legal processes, such as
            responding to subpoenas or requests from government authorities;
          </li>
        </ul>
        <h3 className="h4 mt-4">Disclosure of Information</h3>
        <p className="p">
          We may share information with third parties with your consent, as
          necessary, or as otherwise required or permitted by law, including but
          not limited to: (i) our affiliates and related entities; (ii)
          professional advisors including for the purposes of audits and
          compliance with our legal obligations; (iii) subcontractors and other
          third-party service providers needed to provide the Interface; (iv)
          any potential purchasers or investors in the Company; and (v) entities
          in compliance with any applicable law, regulation, legal process or
          governmental request.
        </p>
        <h3 className="h4 mt-4">
          Third-party collection of information
        </h3>
        <p className="p">
          Our policy only addresses the use and disclosure of information we
          collect from you. To the extent that you disclose your information to
          other parties via the Interface (e.g., by connecting your wallet with
          third-party services or by clicking on a link to any other website or
          location) or via other sites throughout the Internet, different rules
          may apply to their use or disclosure of the information you disclose
          to them. If you are using a compatible third-party digital wallet, you
          agree that you are using such a wallet in accordance with the terms
          and conditions of the applicable provider of such wallet.
        </p>
        <p className="p">
          You acknowledge that we are not responsible for the products,
          services, or descriptions of products or services that you receive
          from third-party sites or to the content or privacy practices of those
          sites, and that this Policy does not apply to any such third-party
          products and services. We expressly disclaim any and all liability for
          actions arising from your use of third-party products and services.
        </p>
        <h3 className="h4 mt-4">Cookies:</h3>
        <p className="p">
          A "cookie" is a small piece of information that a website assigns to
          your device while you are viewing a website. Cookies may be used for
          various different purposes including, among other things, allowing you
          to navigate between pages efficiently, enabling automatic activation
          of certain features, remembering your preferences and making the
          interaction between you and the Interface quicker, easier and
          smoother.
        </p>
        <p className="p">
          The Interface does not use server-side cookies or collect cookie data.
          Any cookies or local storage mechanisms are limited to your browser
          and used solely for functionality from your side. This data is not
          accessed by us, not stored by us, and not shared with any third party.
          You may remove the cookies by following the instructions of your
          device preferences.
        </p>
        <h3 className="h4 mt-4">Data security:</h3>
        <p className="p">
          Although we take reasonable steps to safeguard information, we cannot
          be responsible for the acts of those who gain unauthorized access or
          abuse the Interface, and we make no warranty, express, implied or
          otherwise, that we will prevent such access. We cannot guarantee
          perfect security of your information transmitted through the
          Interface.
        </p>
        <h3 className="h4 mt-4">Data retention:</h3>
        <p className="p">
          We do not store or retain personal data unless you have explicitly
          provided it, such as through a support request. Please note that
          unless you instruct us otherwise, we retain the information we collect
          for as long as needed to provide the Interface and to comply with our
          legal, accounting or reporting obligations, resolve disputes and
          enforce our agreements. With respect to your personal information, you
          may contact us at any time and request: (i) to access, delete, change
          or update any personal information relating to you; or (ii) that we
          will cease any further use of your personal information or that we
          shall remove your personal information (subject to any other legal
          obligation that may require us to keep the information).
        </p>
        <h3 className="h4 mt-4">International transfer:</h3>
        <p className="p">
          Information collected through the Interface may be transferred to,
          processed and stored outside of your home country. Data privacy laws
          in the countries to which your personal information is transferred may
          not be equivalent to the laws in your home country. Your use of the
          Interface or provision of any information therefore constitutes your
          consent to the transfer to and from, processing and storage of your
          information in such jurisdictions.
        </p>
        <h3 className="h4 mt-4">Minors:</h3>
        <p className="p">
          The Interface is not intended for individuals under the age of 18 (or
          other age as required by local law), and we do not knowingly collect
          personal information from children. If you are a parent or guardian
          and believe your child has given personal information to us without
          your consent, you may contact us. If we become aware that a child has
          provided us with personal information in violation of applicable law,
          we will make commercially reasonable efforts to delete such personal
          data and other information in accordance with applicable law.
        </p>
        <h3 className="h4 mt-4">Changes to the Policy:</h3>
        <p className="p">
          We may revise this Policy from time to time at our sole discretion. If
          there are any material changes to this Policy, we will notify you as
          required by applicable law. We encourage you to review this Policy
          regularly for any changes. You understand and agree that you will be
          deemed to have accepted the updated Policy if you continue to use our
          Interface after the new Policy takes effect.
        </p>
        <h3 className="h4 mt-4">How to contact us:</h3>
        <p className="p">
          If you have any questions about the information that we collect about
          you and how we use it, your personal data, or this Policy, please
          contact us at{" "}
          <a
            href="mailto:hello@rujira.network"
            className="color-white hover-primary1">
            hello@rujira.network
          </a>
          .
        </p>
      </div>
    </div>
  </>
);
